import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Modal, ModalBody, ModalHeader } from 'flowbite-react';
import * as sched from 'sched';

interface WorkflowParameterFormProps {
  show: boolean;
  onClose: () => void;
  templateState: sched.WorkflowTemplateState;
  parameterValues: Record<string, any>;
  onSetParameter: (key: string, value: any) => void;
  onRemoveParameter: (key: string) => void;
  /** Save all parameter changes as a single batch. If provided, used instead of individual onSet/onRemove calls. */
  onSaveBatch?: (sets: Array<{ key: string; value: any }>, removes: string[]) => void;
  people: Array<{ id: string; name: string }>;
  teams: Array<{ id: string; name: string }>;
}

interface DiscoveredParam {
  key: string;
  type: 'Bool' | 'Worker' | 'String';
}

export const WorkflowParameterForm: React.FC<WorkflowParameterFormProps> = ({
  show,
  onClose,
  templateState,
  parameterValues,
  onSetParameter,
  onRemoveParameter,
  onSaveBatch,
  people,
  teams,
}) => {
  const discoveredParams = useMemo<DiscoveredParam[]>(() => {
    const arr = sched.discover_workflow_parameters(templateState);
    return Array.from(arr).map((obj: any) => ({
      key: obj.key as string,
      type: obj.type as 'Bool' | 'Worker' | 'String',
    }));
  }, [templateState]);

  // Local draft state — initialized from parameterValues when the modal opens
  const [draft, setDraft] = useState<Record<string, any>>({});
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (show) {
      setDraft({ ...parameterValues });
      setDirty(false);
    }
  }, [show]); // eslint-disable-line react-hooks/exhaustive-deps

  const setDraftValue = useCallback((key: string, value: any) => {
    setDraft(prev => ({ ...prev, [key]: value }));
    setDirty(true);
  }, []);

  const removeDraftValue = useCallback((key: string) => {
    setDraft(prev => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
    setDirty(true);
  }, []);

  const handleSave = useCallback(() => {
    const sets: Array<{ key: string; value: any }> = [];
    const removes: string[] = [];

    for (const param of discoveredParams) {
      const oldVal = parameterValues[param.key];
      const newVal = draft[param.key];

      if (newVal == null && oldVal != null) {
        removes.push(param.key);
      } else if (newVal != null && JSON.stringify(newVal) !== JSON.stringify(oldVal)) {
        sets.push({ key: param.key, value: newVal });
      }
    }

    if (onSaveBatch) {
      onSaveBatch(sets, removes);
    } else {
      for (const { key, value } of sets) onSetParameter(key, value);
      for (const key of removes) onRemoveParameter(key);
    }

    setDirty(false);
    onClose();
  }, [draft, parameterValues, discoveredParams, onSetParameter, onRemoveParameter, onSaveBatch, onClose]);

  const missingCount = discoveredParams.filter(p => draft[p.key] == null).length;

  return (
    <Modal show={show} onClose={onClose} size="lg">
      <ModalHeader>
        Parameters
        {missingCount > 0 && (
          <span className="ml-2 text-xs text-amber-500 font-normal">
            {missingCount} unset
          </span>
        )}
      </ModalHeader>
      <ModalBody>
        {discoveredParams.length === 0 ? (
          <p className="text-sm text-cm-text-muted">This template has no parameters.</p>
        ) : (
          <>
            <div className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-3 items-center">
              {discoveredParams.map(param => (
                <ParameterInput
                  key={param.key}
                  param={param}
                  value={draft[param.key]}
                  onSet={(val) => setDraftValue(param.key, val)}
                  onRemove={() => removeDraftValue(param.key)}
                  people={people}
                  teams={teams}
                />
              ))}
            </div>
            <div className="flex justify-end gap-2 mt-4 pt-4 border-t border-cm-border-subtle">
              <Button color="gray" onClick={onClose}>Cancel</Button>
              <Button color="blue" onClick={handleSave} disabled={!dirty}>
                Save
              </Button>
            </div>
          </>
        )}
      </ModalBody>
    </Modal>
  );
};

/** Renders two grid cells: label (col 1) and input (col 2) */
const ParameterInput: React.FC<{
  param: DiscoveredParam;
  value: any;
  onSet: (value: any) => void;
  onRemove: () => void;
  people: Array<{ id: string; name: string }>;
  teams: Array<{ id: string; name: string }>;
}> = ({ param, value, onSet, onRemove, people, teams }) => {
  const isSet = value != null;
  const labelClass = 'text-sm font-medium text-cm-text-primary whitespace-nowrap';
  const inputClass = 'w-full rounded border border-cm-border-subtle bg-cm-bg-surface px-2 py-1.5 text-sm';

  if (param.type === 'Bool') {
    const boolVal = value?.value ?? false;
    return (
      <>
        <span className={labelClass}>{param.key}</span>
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={isSet && boolVal}
            onChange={(e) => onSet({ type: 'Bool', value: e.target.checked })}
            className="rounded border-cm-border-subtle"
          />
          {isSet && (
            <button onClick={onRemove} className="text-xs text-cm-text-muted hover:text-red-400">
              Unset
            </button>
          )}
        </div>
      </>
    );
  }

  if (param.type === 'Worker') {
    const workerRef = value?.value;
    const workerType = workerRef?.type;
    const workerId = workerRef?.id || '';

    return (
      <>
        <span className={labelClass}>{param.key}</span>
        <select
          value={workerId ? `${workerType}:${workerId}` : ''}
          onChange={(e) => {
            const val = e.target.value;
            if (!val) { onRemove(); return; }
            const [type, ...idParts] = val.split(':');
            const id = idParts.join(':');
            onSet({ type: 'Worker', value: { type, id } });
          }}
          className={inputClass}
        >
          <option value="">-- Select assignee --</option>
          {people.length > 0 && (
            <optgroup label="People">
              {people.map(p => (
                <option key={p.id} value={`Person:${p.id}`}>{p.name}</option>
              ))}
            </optgroup>
          )}
          {teams.length > 0 && (
            <optgroup label="Teams">
              {teams.map(t => (
                <option key={t.id} value={`Team:${t.id}`}>{t.name}</option>
              ))}
            </optgroup>
          )}
        </select>
      </>
    );
  }

  // String type
  const strVal = value?.value ?? '';
  return (
    <>
      <span className={labelClass}>{param.key}</span>
      <input
        type="text"
        value={strVal}
        onChange={(e) => onSet({ type: 'String', value: e.target.value })}
        placeholder={`Value for ${param.key}`}
        className={inputClass}
      />
    </>
  );
};

export default WorkflowParameterForm;
